import { buildBoatDetailHref } from '../boat-detail-url';
import { isSafeUrl } from '../url-safety';

export interface BoatCardData {
  stock: string;
  year: number;
  make: string;
  model: string;
  id?: string;
  marketing_model?: string;
  length_ft?: number;
  price?: number;
  status: 'in-stock' | 'on-order' | 'sold';
  location?: string;
  image_url?: string;
  detail_url?: string;
  highlights?: string[];
  cta_label?: string;
}

interface BoatCardProps {
  data: BoatCardData;
}

const STATUS_LABELS: Partial<Record<string, string>> = {
  'in-stock': 'In Stock',
  'on-order': 'On Order',
  sold: 'Sold',
};

function formatPrice(price: number): string {
  return price.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

export function BoatCard({ data }: BoatCardProps): React.JSX.Element {
  const { year, make, model, id, marketing_model, price, status, location, image_url, detail_url, highlights, cta_label } = data;
  const ctaHref = buildBoatDetailHref({ id, make, marketing_model, detail_url });
  const ctaLabel = cta_label ?? 'View Details';
  const visibleHighlights = highlights?.slice(0, 4);
  const statusLabel = STATUS_LABELS[status];
  const safeImageUrl = isSafeUrl(image_url) ? image_url : undefined;
  const titleParts = [year, make, model].filter(Boolean).join(' ');

  function handleImageError(e: React.SyntheticEvent<HTMLImageElement>): void {
    e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="60" viewBox="0 0 100 60"%3E%3Crect width="100" height="60" fill="%23e2e8f0"/%3E%3C/svg%3E';
  }

  return (
    <div className="boat-card">
      {safeImageUrl && (
        <img
          className="boat-card-image"
          src={safeImageUrl}
          alt={titleParts}
          onError={handleImageError}
        />
      )}
      <div className="boat-card-body">
        <div className="boat-card-header">
          <span className="boat-card-title">{titleParts}</span>
          {statusLabel && (
            <span className={`boat-card-status boat-card-status-${status}`}>{statusLabel}</span>
          )}
        </div>
        {price !== undefined && (
          <div className="boat-card-price">{formatPrice(price)}</div>
        )}
        {location && (
          <div className="boat-card-location">{location}</div>
        )}
        {visibleHighlights && visibleHighlights.length > 0 && (
          <ul className="boat-card-highlights">
            {visibleHighlights.map((h, i) => (
              <li key={i}>{h}</li>
            ))}
          </ul>
        )}
        {ctaHref && (
          <a
            className="boat-card-cta"
            href={ctaHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            {ctaLabel}
          </a>
        )}
      </div>
    </div>
  );
}
